import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search } from "lucide-react";

const LangLat = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [tenant, setTenant] = useState("");
  const [station, setStation] = useState("");
  const [noLatLong, setNoLatLong] = useState(undefined);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState([]);
  const [stationsList, setStationsList] = useState([]);
  const [tenantsList, setTenantsList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("origo_tenant_id");
  const [sortDirection, setSortDirection] = useState("ASC");

  // Add these options near the top of the component:
  const rowLimitOptions = [25, 50, 100];

  const token = localStorage.getItem("origo_access_token");
  const fetchedOnce = useRef(false);

  // Fetch paginated stations for table
  const fetchData = async (
    tenantId = "",
    stationId = "",
    pageNo = 1,
    keyword = "",
    noLatLongFlag = undefined,
    rowLimit = limit,
    sortFieldParam = sortField,
    sortDirectionParam = sortDirection
  ) => {
    try {
      const params = {
        page: pageNo,
        limit: rowLimit,
        sortBy: `[[\"${sortFieldParam}\",\"${sortDirectionParam}\"]]`
      };
      if (tenantId) params.tenant = tenantId;
      if (stationId) params.station = stationId;
      if (keyword) params.keyword = keyword;
      if (noLatLongFlag !== undefined) params.noLatLong = noLatLongFlag;

      const res = await axios.get(
        `${process.env.REACT_APP_ORIGO_BASE_URL}config/stations`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      const stations = Array.isArray(res.data?.data?.stationsData)
        ? res.data.data.stationsData
        : [];

      setData(stations);
      setTotalRecords(res.data?.data?.totalCount || 0);
      setStationsList(stations);

      // Extract unique tenants
      const tenantsMap = new Map();
      stations.forEach((st) => {
        if (st.tenant) tenantsMap.set(st.tenant.id, st.tenant);
      });
      setTenantsList(Array.from(tenantsMap.values()));
    } catch (err) {
      console.error("Error fetching stations:", err);
      setData([]);
      setTotalRecords(0);
      setStationsList([]);
      setTenantsList([]);
    }
  };

  // Fetch all tenants and all stations (for dropdowns)
  // const fetchAllTenantsFromStations = async () => {
  //   try {
  //     const res = await axios.get(
  //       `${process.env.REACT_APP_ORIGO_BASE_URL}config/stations`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //         params: { page: 1, limit: 1000 },
  //       }
  //     );

  //     const stations = Array.isArray(res.data?.data?.stationsData)
  //       ? res.data.data.stationsData
  //       : [];

  //     setStationsList(stations);

  //     // Extract unique tenants
  //     const tenantsMap = new Map();
  //     stations.forEach((st) => {
  //       if (st.tenant) tenantsMap.set(st.tenant.id, st.tenant);
  //     });
  //     setTenantsList(Array.from(tenantsMap.values()));
  //   } catch (err) {
  //     console.error("Error fetching tenants:", err);
  //     setStationsList([]);
  //     setTenantsList([]);
  //   }
  // };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortDirection("ASC");
    }

    // fetchData(
    //   tenant,
    //   station,
    //   page,
    //   searchTerm,
    //   noLatLong,
    //   limit,
    //   field,
    //   sortDirection === "ASC" ? "DESC" : "ASC"
    // );
  };

  // Initial load
  useEffect(() => {
    if (!fetchedOnce.current) {
      // fetchAllTenantsFromStations();
      fetchData("", "", 1, "", undefined, limit, sortField, sortDirection);
      fetchedOnce.current = true;
    }
  }, []);

  // Filters + search + pagination
  useEffect(() => {
    if (!fetchedOnce.current) return;

    const handler = setTimeout(() => {
      fetchData(
        tenant,
        station,
        page,
        searchTerm,
        noLatLong,
        limit,
        sortField,
        sortDirection
      );
    }, 300);

    return () => clearTimeout(handler);
  }, [tenant, station, page, noLatLong, searchTerm, limit, sortDirection, sortField]);

  const totalPages = Math.ceil(totalRecords / limit);

  return (
    <div className="flex flex-col gap-2 overflow-hidden h-full">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 flex items-center gap-4 relative z-50">
        <div className="flex flex-wrap items-center gap-4">
          {/* Tenant Select */}
          <select
            className="select select-bordered w-48 select-sm bg-white dark:bg-gray-800 text-[#656565] dark:text-gray-300 text-sm h-full rounded-sm dark:border-gray-700"
            value={tenant}
            onChange={(e) => {
              setTenant(e.target.value);
              setStation("");
              setPage(1);
            }}
          >
            <option value="">All Tenants</option>
            {tenantsList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.origo_tenant_name}
              </option>
            ))}
          </select>

          {/* Station Select */}
          <select
            className="select select-bordered w-48 select-sm bg-white dark:bg-gray-800 disabled:bg-gray-400 disabled:text-gray-800 text-[#656565] dark:text-gray-300 text-sm h-full rounded-sm dark:border-gray-700"
            value={station}
            onChange={(e) => {
              setStation(e.target.value);
              setPage(1);
            }}
            disabled={!tenant}
          >
            <option value="">All Stations</option>
            {stationsList
              .filter(
                (st) => !tenant || st.origo_tenant_id === parseInt(tenant)
              )
              .map((st) => (
                <option key={st.id} value={st.id}>
                  {st.origo_station_name}
                </option>
              ))}
          </select>
        </div>
        {/* No Lat/Long */}
        <div className="flex flex-col">
          <div className="text-[#3A3737BF] dark:text-gray-400 text-xs">
            Latitude/Longitude
          </div>
          <label className="label cursor-pointer flex items-center gap-1 p-0">
            <input
              type="checkbox"
              className="checkbox checkbox-primary checkbox-xs rounded-sm"
              checked={noLatLong ?? false}
              onChange={(e) =>
                setNoLatLong(e.target.checked ? true : undefined)
              }
            />
            <span className="text-sm font-light dark:text-gray-300">
              No Lat/Long
            </span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 flex flex-col gap-4 h-full overflow-hidden">
        {/* Search Bar aligned with records */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-base font-semibold text-black dark:text-white">
            Latitude / Longitude
          </h2>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="text-xs text-[#3A3737] dark:text-gray-400 font-semibold whitespace-nowrap">
              {Math.min(page * limit, totalRecords)} / {totalRecords} records
            </div>

            <div className="bg-[#DBDBDB80] dark:bg-gray-700 h-12 w-[1px]"></div>
            <div className="border-[#F8F8F8] dark:border-gray-700 border-b px-6 py-3.5 flex gap-2 items-center w-full sm:w-auto">
              <Search size={16} className="text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search by Station Name / ID"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="input input-ghost input-xs w-full sm:w-64 font-thin text-[#747474] dark:text-gray-300 placeholder:text-xs dark:placeholder:text-gray-500 focus:outline-none px-0 dark:bg-gray-800 dark:focus:text-white"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto flex-1 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="min-w-[800px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700 text-xs text-[#3A3737] dark:text-gray-300 font-medium">
                  <th
                    className={`bg-[#F8F8F8] dark:bg-gray-900 text-xs text-left py-4 px-6 border-r dark:border-gray-700 whitespace-nowrap ${
                       "cursor-pointer select-none" 
                    }`}
                    onClick={() => handleSort("origo_tenant_id")}
                  >
                    <div className="flex items-center gap-2 justify-between">
                      Tenant Name
                      {
                        <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          {sortField === "origo_tenant_id" ? (
                            sortDirection === "ASC" ? (
                              <span className="flex items-center">
                                A-Z <span className="text-xl ml-1">↑</span>
                              </span>
                            ) : (
                              <span className="flex items-center">
                                Z-A <span className="text-xl ml-1">↓</span>
                              </span>
                            )
                          ) : (
                            <span className="text-lg">↕</span>
                          )}
                        </span>
                      }
                    </div>
                  </th>
                  <th
                    className={`bg-gray-50 dark:bg-gray-900 text-left py-4 px-6 border-r dark:border-gray-700 whitespace-nowrap ${
                      "cursor-pointer select-none" 
                    }`}
                    onClick={() => handleSort("origo_station_name")}
                  >
                    <div className="flex items-center gap-2 justify-between">
                      Station Name
                      {
                        <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          {sortField === "origo_station_name" ? (
                            sortDirection === "ASC" ? (
                              <span className="flex items-center">
                                A-Z <span className="text-xl ml-1">↑</span>
                              </span>
                            ) : (
                              <span className="flex items-center">
                                Z-A <span className="text-xl ml-1">↓</span>
                              </span>
                            )
                          ) : (
                            <span className="text-lg">↕</span>
                          )}
                        </span>
                      }
                    </div>
                  </th>
                  <th
                    className={`bg-gray-50 dark:bg-gray-900 text-left py-4 px-6 border-r dark:border-gray-700 whitespace-nowrap ${
                    ""
                    }`}
                    // onClick={() => !noLatLong && handleSort("latlong")}
                  >
                    <div className="flex items-center gap-2 justify-between">
                      Lat / Long
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {data.length > 0 ? (
                  data.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-[#3A3737] dark:text-gray-300"
                    >
                      <td className="py-4 px-6 border-r dark:border-gray-700 whitespace-nowrap">{`${
                        item.tenant?.origo_tenant_name ?? "-"
                      } (${item.tenant?.origo_tenant_id})`}</td>
                      <td className="py-4 px-6 border-r dark:border-gray-700 whitespace-nowrap">{`${item.origo_station_name} (${item.origo_station_id})`}</td>
                      <td className="py-4 px-6 border-r dark:border-gray-700 whitespace-nowrap">
                        {item.latitude ?? "-"}, {item.longitude ?? "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="text-center py-6 text-gray-400 dark:text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center pt-2">
          {/* Row limit selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Rows per page:
            </span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1); // Reset to first page when changing limit
              }}
              className="select select-bordered select-sm bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700"
            >
              {rowLimitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="btn btn-sm btn-outline dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Prev
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="btn btn-sm btn-outline dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LangLat;
